import { type FC, useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  DeleteJobCatalogNodeAttributeDocument,
  NodeCompetenciesDataCollectionDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { formatUuidToNodeType, getNodeId } from 'modules/job_catalog/lib/nodesUtils'

const UnassignCompetencyModal: FC<{
  nodeAttributeId: number
  competencyName: string
  nodeUuid: string
  onClose: () => void
}> = ({ nodeAttributeId, competencyName, nodeUuid, onClose }) => {
  const [inputName, setInputName] = useState<string>('')
  const isInputOk = inputName.trim() === competencyName?.trim()

  const [unassignAttribute, { loading }] = useMutation(DeleteJobCatalogNodeAttributeDocument, {
    extractorFn: (data) => data.jobCatalogMutations.deleteJobcatalogNodeAttribute,
    refetchQueries: [NodeCompetenciesDataCollectionDocument],
    awaitRefetchQueries: true,
  })

  const unassign = async () => {
    const response = await unassignAttribute({
      variables: {
        id: nodeAttributeId,
        nodeId: getNodeId(nodeUuid),
        nodeType: formatUuidToNodeType(nodeUuid),
      },
    })

    const handled = handleResult(response, () => null)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('job_catalog.nodes.competency.unassign_modal.success'),
      })
      onClose()
      return handled
    }
  }

  return (
    <Modal
      title={i18n.t('job_catalog.nodes.competency.unassign_modal.title')}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={loading || !isInputOk} submit>
            {i18n.t('common.delete')}
          </Button>
        </>
      }
      illustration="alert"
      onClose={onClose}
      onSubmit={unassign}
    >
      <Box flexDirection="column" gap="s8">
        <Text.Normal whiteSpace="breakSpaces">
          {i18n.t('job_catalog.nodes.competency.unassign_modal.description', {
            name: competencyName,
          })}
        </Text.Normal>
        <TextField onChange={setInputName} value={inputName} />
      </Box>
    </Modal>
  )
}

export default UnassignCompetencyModal
